import { TopBarDropdownContextProps } from 'Components/TopBar/contexts/TopBarDropdownContext';
import { DropDownItemTopBar } from 'Components/TopBar/DropDownItemTopBar';
import { ContactModelV2 } from 'Models/ContactModelV2';
import type {
  ContactModel,
  ConversationModel,
  IContactDto,
  IConversationModel,
  IPersonModel,
  PersonModel,
  SearchResult,
} from 'Models/index';
import React from 'react';
import type { ConversationStore } from 'Stores/index';
import { SearchableContactModelV2 } from 'Stores/SearchStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import { createArrayOfPersonIds } from '../helpers/createArrayOfPersonIds';

export type createConversationsOptionsProps = (
  allUsersButNotCurrent:
    | IPersonModel[]
    | SearchableContactModelV2[]
    | {
        source: IPersonModel | IContactDto | IConversationModel;
        match: string;
        score: number;
      }[]
    | SearchResult<
        | PersonModel
        | SearchableContactModelV2
        | ConversationModel
        | ContactModel
      >[],
  changeConvTopic: ConversationStore['changeConvTopic'],
  isNumericSearch?: boolean,
  searchQuery?: string
) => TopBarDropdownContextProps['options'];

type PersonVariant = 'group' | 'internal-contact';

// Helper function to get the variant
const getVariant = (
  person: PersonModel | SearchableContactModelV2
): PersonVariant => {
  return person.searchableType === 'SearchableDetailsConversation'
    ? 'group'
    : 'internal-contact';
};

// Helper function to get the person's full name
const getPersonFullName = (person: PersonModel | ContactModelV2): string => {
  return person.firstName && person.lastName
    ? `${person.firstName} ${person.lastName}`
    : person.firstName || person.lastName || '';
};

// Helper function to get the name
const getName = (
  person,
  variant: PersonVariant,
  changeConvTopic: ConversationStore['changeConvTopic']
) => {
  if (variant === 'group') {
    return (
      (person.topic as string) ||
      changeConvTopic(createArrayOfPersonIds(person.participants))
    );
  }

  return (
    getPersonFullName(person) ||
    formatNumberWithNationalCode(person.mobileNumber) ||
    person.mobileNumber
  );
};

// Refactored createConversationsOptions function
export const createConversationsOptions: createConversationsOptionsProps = (
  allUsersButNotCurrent,
  changeConvTopic,
  isNumericSearch,
  searchQuery
) => {
  return (
    allUsersButNotCurrent?.map((personSearch) => {
      const person =
        personSearch.source && typeof personSearch.source !== 'string'
          ? personSearch.source
          : (personSearch as PersonModel | SearchableContactModelV2);

      const variant = getVariant(person);
      const name = getName(person, variant, changeConvTopic);

      const id =
        variant === 'group'
          ? person.id.toString()
          : (person as SearchableContactModelV2).sourceId;

      return {
        key: id,
        value: id,
        text: isNumericSearch ? searchQuery : name,
        variant,
        label: (
          <DropDownItemTopBar
            key={id}
            {...(variant === 'group'
              ? { convType: 'Channel' }
              : {
                  userInfo:
                    person.firstName || person.lastName
                      ? {
                          personId: id,
                          keyValue: id,
                        }
                      : {
                          phoneNumbers: [person.phoneNumber],
                          keyValue: person.phoneNumber,
                        },
                })}
            organization={person.department}
            ignoreName={!(person.firstName || person.lastName)}
            role={person.role}
            {...{ variant, name }}
          />
        ),
      };
    }) || []
  );
};
